import { KaboodleToaster } from "components/common";
import { useRef, useState } from "react";
import {
  useGetMetadocMetadataQuery,
  useUpdateMetadocMetadataMutation,
} from "services/metadoc-api";

import type { MetadocMetadataValue } from "services/metadoc-api";

export type NonQualifiedItem = {
  id: number;
  content: string;
  date: string;
  onChange: (value: string) => void;
  onBlurSave: () => Promise<void>;
  onDateChange: (value: string) => void;
  onDateBlurSave: () => Promise<void>;
};

type UseNonQualifiedItemsDocumentsArgs = {
  metadocId: number;
  shouldFetch: boolean;
};

type UseNonQualifiedItemsDocumentsReturn = {
  items: NonQualifiedItem[];
  isLoading: boolean;
  isFetching: boolean;
};

const extractContent = (value: MetadocMetadataValue): string => {
  if (!value) return "";
  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    if (
      parsed &&
      typeof parsed === "object" &&
      !Array.isArray(parsed) &&
      Array.isArray((parsed as Record<string, unknown>).items)
    ) {
      return ((parsed as Record<string, unknown>).items as string[]).join("\n");
    }
  } catch {
    // ignore parse errors
  }
  return typeof value === "string" ? value : "";
};

export const useNonQualifiedItemsDocuments = ({
  metadocId,
  shouldFetch,
}: UseNonQualifiedItemsDocumentsArgs): UseNonQualifiedItemsDocumentsReturn => {
  const {
    data: rawMetadata = [],
    isLoading,
    isFetching,
  } = useGetMetadocMetadataQuery(
    { metadocId, type: "non_qualified_item" },
    { skip: !shouldFetch, refetchOnMountOrArgChange: true },
  );

  const [drafts, setDrafts] = useState<
    Record<number, { content: string; date: string }>
  >({});
  // Use ref so blur handlers always see the latest draft state
  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;

  const [updateMetadocMetadata] = useUpdateMetadocMetadataMutation();

  const items: NonQualifiedItem[] = rawMetadata.map((row) => {
    const serverContent = extractContent(row.value);
    const serverDate = row.date ?? "";
    const draft = drafts[row.id];
    const content = draft?.content ?? serverContent;
    const date = draft?.date ?? serverDate;

    const onChange = (value: string) => {
      setDrafts((prev) => ({
        ...prev,
        [row.id]: {
          content: value,
          date: prev[row.id]?.date ?? serverDate,
        },
      }));
    };

    const onDateChange = (value: string) => {
      setDrafts((prev) => ({
        ...prev,
        [row.id]: {
          content: prev[row.id]?.content ?? serverContent,
          date: value,
        },
      }));
    };

    const onBlurSave = async () => {
      const currentContent = draftsRef.current[row.id]?.content;
      if (currentContent === undefined || currentContent === serverContent) {
        return;
      }
      const updatedItems = currentContent
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
      try {
        await updateMetadocMetadata({
          metadataId: row.id,
          data: { value: { items: updatedItems } },
        }).unwrap();
      } catch (error) {
        console.error("Failed to save non-qualified item:", error);
        KaboodleToaster.error("Failed to save content. Please try again.");
      }
    };

    const onDateBlurSave = async () => {
      const currentDate = draftsRef.current[row.id]?.date;
      if (currentDate === undefined || currentDate === serverDate) {
        return;
      }
      try {
        await updateMetadocMetadata({
          metadataId: row.id,
          data: { date: currentDate || null },
        }).unwrap();
      } catch (error) {
        console.error("Failed to save non-qualified item date:", error);
        KaboodleToaster.error("Failed to save date. Please try again.");
      }
    };

    return {
      id: row.id,
      content,
      date,
      onChange,
      onDateChange,
      onBlurSave,
      onDateBlurSave,
    };
  });

  return { items, isLoading, isFetching };
};
